/* eslint-disable no-warning-comments */
import React, { forwardRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Link as RouterLink, useLocation, useHistory } from 'react-router-dom'
import { trackLink } from '@fs/zion-analytics'
import { css } from '@emotion/core'
import { boolAttr } from '@fs/zion-frontend-friends'
import { createLocation } from 'history'
import LeavingLinkInterceptor, { isUrlAllowed } from '../global/LeavingLinkInterceptor'

const hasWindow = typeof window !== 'undefined'

const linkCss = css`
  &[no-link-styles] {
    &,
    &:hover,
    &:visited {
      color: currentcolor;
      text-decoration: none;
    }
    &:focus {
      outline: none;
    }
  }
`

// the next 2 functions were copied out of the locationUtils file that the react-router-dom uses to resolve it's links.
// https://github.com/ReactTraining/react-router/blob/e951e1d78edacbdf72c458e97938f92da3a43512/packages/react-router-dom/modules/utils/locationUtils.js
const resolveToLocation = (to, currentLocation) => (typeof to === 'function' ? to(currentLocation) : to)
const normalizeToLocation = (to, currentLocation) => {
  return typeof to === 'string' ? createLocation(to, null, null, currentLocation) : to
}

const RouterLinkWrapper = ({ to, linkClick, noLinkStyles, children, linkRef, wayfinding, ...rest }) => {
  const location = useLocation()
  const history = useHistory()
  const urlLocation = normalizeToLocation(resolveToLocation(to, location), location)
  const endHref = urlLocation ? history.createHref(urlLocation) : ''

  return (
    <RouterLink
      to={to}
      onClick={(e) => {
        linkClick(e, endHref)
      }}
      innerRef={linkRef}
      no-link-styles={boolAttr(noLinkStyles)}
      wayfinding={boolAttr(wayfinding)}
      css={linkCss}
      {...rest}
    >
      {children}
    </RouterLink>
  )
}

export const Link = forwardRef(
  (
    {
      to,
      external,
      action = 'click_action',
      linkName,
      children,
      onClick,
      noScrollTop = false,
      wayfinding = false,

      /** [HIDDEN]: For internal Zion-UI use only */
      noLinkStyles,
      enableLeavingInterceptor = true,
      ...rest
    },
    ref
  ) => {
    const toIsString = typeof to === 'string' // can also be an object or function
    const [isOpen, setIsOpen] = useState()

    // to can be a object or function https://reacttraining.com/react-router/web/api/Link
    const isFullUrl = toIsString && to.match(/^https?:\/\//)
    const isExternal = external || isFullUrl

    // TODO: what do we want to do about local development? How/where should it link to?
    // TODO: make a stand alone function to create the URL itself, expose it, and use it here as well
    // TODO: what if I forget to put a leading '/' ?
    // TODO: What if I want this to be relative? Is that a thing, or not allowed?
    // TODO: Language prefix is happening sometime, right?

    // note: isAbsolute is used to define url which only used if the link is external. If the link is external it doesn't need to be wrapped in <Route>. In order to resolve the object or function version of to we have to use hooks that can only be used when wrapped in <Route>.
    const isAbsolute = toIsString && to.indexOf('/') === 0
    // Prepending the origin is important in the scenario where you are routing patrons to the react app through experiments/cookies
    const url = isAbsolute && hasWindow ? window.location.origin + to : to

    const linkClick = (event, urlToTrack) => {
      if (!isExternal && !noScrollTop) {
        hasWindow && window.scrollTo(0, 0)
      }
      if (linkName) {
        trackLink(null, urlToTrack, action, linkName)
      }
      onClick?.(event)
    }

    const shouldOpenLeavingDialog = enableLeavingInterceptor && !isUrlAllowed(url)

    if (isExternal) {
      return (
        <>
          <a
            href={url}
            onClick={(e) => {
              if (shouldOpenLeavingDialog) {
                e.preventDefault()
                setIsOpen(true)
              }
              linkClick(e, url)
            }}
            ref={ref}
            no-link-styles={boolAttr(noLinkStyles)}
            wayfinding={boolAttr(wayfinding)}
            css={linkCss}
            data-external
            {...rest}
          >
            {children}
          </a>
          {shouldOpenLeavingDialog && (
            <LeavingLinkInterceptor isOpen={isOpen} setIsOpen={setIsOpen} externalHref={url} />
          )}
        </>
      )
    }
    return (
      <RouterLinkWrapper
        to={to}
        linkClick={linkClick}
        linkRef={ref}
        noLinkStyles={noLinkStyles}
        wayfinding={wayfinding}
        {...rest}
      >
        {children}
      </RouterLinkWrapper>
    )
  }
)
export default Link

Link.propTypes = {
  /** Path to the route. Example: to="/frontier/app/react". See https://reacttraining.com/react-router/web/api/Link for all options. */
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]).isRequired,
  /** For using Link outside of the Router. This prop is not needed for full urls. */
  external: PropTypes.bool,
  /** Action you'd like to see for this link on the analytics dashboard */
  action: PropTypes.string,
  /** If specified, and analytics is enabled for the react app, then analytics click metrics will be sent. The metrics will contain the specified value, which will be available in analytics reports.  */
  linkName: PropTypes.string,
  /** Optional way to also add an onclick to your link */
  onClick: PropTypes.func,
  /** If true this will prevent the page from scrolling to the top when clicked. Only works on internal links. */
  noScrollTop: PropTypes.bool,
  /** When set to true, the link will always be the primary link color even when visited */
  wayfinding: PropTypes.bool,
}
